import React, { useEffect, useState } from 'react';
import { Card, Tag, Space, Typography, Button, Modal, Alert, Spin, Empty } from 'antd';
import { useSharedDashboardLinks, linkStatus } from '../hooks/useSharedDashboardLinks';
import { formatDdMmYyyy, formatDdMmYyyyTime } from '../utils/formatDdMmYyyy';

const { Text } = Typography;

const STATUS_TAG = {
  active: { color: 'green', label: 'Active' },
  expired: { color: 'default', label: 'Expired' },
  revoked: { color: 'red', label: 'Revoked' },
  legacy_reissue: { color: 'orange', label: 'Legacy link: re-issue' },
};

/**
 * "My shared links" - the SPOC's list of the shareable dashboard links they created for this project,
 * with early revoke (Commandment VI/III: a company controls its own external disclosures; closes
 * lesson share-link-create-without-manage-surface). Rendered only for the Company SPOC (or a Lead
 * Advisor acting as one); the API is the real gate. No link URL is ever shown here - see
 * hooks/useSharedDashboardLinks.js. `refreshKey` re-reads the list after a new link is created.
 * `legacy_reissue` links cannot be opened any more (only a digest is stored); the SPOC is offered
 * `onCreateNew` instead. Access facts that the server reports as null are shown as "Unknown".
 */
const MySharedDashboardLinks = ({ projectId, tiles, refreshKey, onCreateNew }) => {
  const { status, links, errorMessage, reload, revoke } = useSharedDashboardLinks(projectId);
  const [confirmLink, setConfirmLink] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState(null);

  useEffect(() => {
    reload();
    // Keyed on project + refreshKey on purpose: `reload` changes identity with the MSAL account, and
    // the hooks lint rule is not loaded by this build (see eslint-plugin-react-hooks-breaks-cra-build).
  }, [projectId, refreshKey]);

  const titleFor = (tileId) => (tiles || []).find((t) => t.tileId === tileId)?.title || tileId;

  const closeConfirm = () => {
    setConfirmLink(null);
    setRevokeError(null);
  };

  const handleRevoke = async () => {
    setRevoking(true);
    setRevokeError(null);
    const result = await revoke(confirmLink.id);
    setRevoking(false);
    if (result.ok) closeConfirm();
    else setRevokeError(result.errorMessage);
  };

  const now = Date.now();

  return (
    <Card size="small" title="My shared links" data-testid="my-shared-links" style={{ marginTop: 24 }}>
      {status === 'loading' && <Spin />}
      {status === 'error' && (
        <Space direction="vertical">
          <Alert type="warning" showIcon message="Your shared links could not be loaded" description={errorMessage} />
          <Button onClick={reload}>Retry</Button>
        </Space>
      )}
      {status === 'empty' && <Empty description="You have not shared this project's dashboard yet." />}
      {status === 'ready' && (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {links.map((link) => {
            const st = linkStatus(link, now);
            const tag = STATUS_TAG[st];
            return (
              <div key={link.id} data-testid={`shared-link-${link.id}`} style={{ borderBottom: '1px solid var(--color-border)', paddingBottom: 12 }}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Space>
                    <Tag color={tag.color} data-testid="shared-link-status">
                      {tag.label}
                    </Tag>
                    {link.label && <Text strong data-testid="shared-link-label">{link.label}</Text>}
                    {link.passwordProtected !== null && (
                      <Tag data-testid="shared-link-password">{link.passwordProtected ? 'Password protected' : 'No password'}</Tag>
                    )}
                  </Space>
                  <Text data-testid="shared-link-tiles">Tiles: {link.tileIds.length ? link.tileIds.map(titleFor).join(', ') : 'none recorded'}</Text>
                  <Text type="secondary" data-testid="shared-link-dates">
                    Created {formatDdMmYyyy(link.createdAt)}
                    {st === 'revoked' ? ` · Revoked ${formatDdMmYyyy(link.revokedAt)}` : ` · Expires ${formatDdMmYyyy(link.expiresAt)}`}
                  </Text>
                  {st !== 'legacy_reissue' && (
                    <Text type="secondary" data-testid="shared-link-activity">
                      Last opened: {link.lastAccessedAt ? formatDdMmYyyyTime(link.lastAccessedAt) : 'Unknown'} · Times opened:{' '}
                      {link.accessCount === null ? 'Unknown' : link.accessCount}
                    </Text>
                  )}
                  {st === 'active' && link.aiTilesAtRisk.length > 0 && (
                    <Text type="warning" data-testid="shared-link-ai-risk">
                      Not shown publicly on this link: {link.aiTilesAtRisk.map(titleFor).join(', ')}. AI tiles appear to the public only when a Lead
                      Advisor has directly reviewed them; auto-refreshed values are withheld.
                    </Text>
                  )}
                  {st === 'legacy_reissue' && (
                    <Space direction="vertical" size={4}>
                      <Text data-testid="shared-link-legacy-note">
                        This older link can no longer be opened or shown again. Create a new link to share this dashboard.
                      </Text>
                      {onCreateNew && (
                        <Button size="small" type="primary" data-testid="shared-link-create-new" onClick={onCreateNew}>
                          Create a new link
                        </Button>
                      )}
                    </Space>
                  )}
                  {st === 'active' && (
                    <Button size="small" danger data-testid="shared-link-revoke" onClick={() => setConfirmLink(link)}>
                      Revoke link
                    </Button>
                  )}
                </Space>
              </div>
            );
          })}
        </Space>
      )}

      <Modal
        open={!!confirmLink}
        title="Revoke this shared link?"
        onCancel={closeConfirm}
        footer={
          <Space>
            <Button onClick={closeConfirm}>Keep link</Button>
            <Button danger type="primary" loading={revoking} data-testid="shared-link-revoke-confirm" onClick={handleRevoke}>
              Revoke link
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Anyone holding this link will lose access immediately. This cannot be undone; you would need to generate a new link.</Text>
          {revokeError && <Alert type="error" showIcon message="Could not revoke the link" description={revokeError} />}
        </Space>
      </Modal>
    </Card>
  );
};

export default MySharedDashboardLinks;
